"use client";

import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { MessageContentFunctionCall } from "@/services/wingman";
import Icon from "@/components/library/Icon";
import CodeExecutionGroup from "./CodeExecutionGroup";
import { getToolConfig } from "./toolCallConfig";
import { type ToolCallStep, isCodeExecutionTool } from "./toolCallUtils";
import { ToolCallRow } from "@repo/ui/gailgpt/tool-call-row";

interface AskToolCallProps {
  data: MessageContentFunctionCall;
  isCompleted?: boolean;
  result?: unknown;
  /** When multiple calls are collapsed, shows a summarized count label. */
  count?: number;
  steps?: ToolCallStep[];
}

export default function AskToolCall({
  data,
  isCompleted = false,
  result,
  count,
  steps,
}: Readonly<AskToolCallProps>) {
  const { t } = useTranslation("ask");
  const functionName = data.functionName ?? "default";
  const normalizedSteps = steps?.length ? steps : [{ data, isCompleted, result }];

  if (
    normalizedSteps.length >= 1 &&
    normalizedSteps.every((step) => isCodeExecutionTool(step.data.functionName ?? ""))
  ) {
    return <CodeExecutionGroup steps={normalizedSteps} isCompleted={isCompleted} />;
  }

  const config = getToolConfig(functionName, t, data.parameters, result, data.presentationUnavailable ? undefined : data.presentation);

  const displayText = isCompleted ? config.completedText : config.inProgressText;

  // Build the label text
  let labelText: string;
  if (!isCompleted) {
    labelText = displayText;
  } else if (count && count > 1) {
    const countTextKey = config.countTextKey ?? "tools.phrases.stepCount";
    labelText = `${config.completedGroupText ?? config.completedText} (${t(countTextKey, { count })})`;
  } else {
    labelText = config.subtitle ?? displayText;
  }

  return (
    <ToolCallRow
      completed={isCompleted}
      label={labelText}
      icon={
        <Icon
          icon={config.icon}
          className={clsx(
            "h-3 w-3",
            !isCompleted && "animate-text-shimmer"
          )}
        />
      }
    />
  );
}
